import { useEffect, useReducer, useRef } from 'react';
import { serverDate } from '@/lib/time/serverClock';

// setTimeout overflows above 2^31-1 ms (~24.8 days) and fires immediately.
const MAX_DELAY_MS = 2_147_483_647;
// Fire a moment after the edge so windowStatus() already sees it crossed.
const AFTER_EDGE_MS = 1_000;

/**
 * Re-renders (or calls `onCross`) when the server clock passes the next of
 * `edges` – e.g. a test's opening or closing time. Availability is computed at
 * render time, so without this a test that opens while the page is open stays
 * "upcoming" until something else happens to re-render. One timer, no polling,
 * no requests of its own. Also re-checks when the tab becomes visible again,
 * since timers in background tabs and sleeping phones fire late or not at all.
 */
export function useTimeBoundaries(
  edges: ReadonlyArray<Date | string | null | undefined>,
  onCross?: () => void,
): void {
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  const onCrossRef = useRef(onCross);
  useEffect(() => { onCrossRef.current = onCross; });

  // Stable dependency: the set of future edge times, not the array identity.
  const key = edges
    .map((e) => (e ? new Date(e).getTime() : NaN))
    .filter((ms) => Number.isFinite(ms))
    .sort((a, b) => a - b)
    .join(',');

  useEffect(() => {
    if (!key) return;
    const times = key.split(',').map(Number);
    let timer: ReturnType<typeof setTimeout> | undefined;
    let next: number | undefined;

    const fire = () => {
      if (onCrossRef.current) onCrossRef.current();
      else rerender();
    };

    const arm = () => {
      if (timer) clearTimeout(timer);
      const now = serverDate().getTime();
      next = times.find((ms) => ms > now);
      if (next === undefined) return;
      timer = setTimeout(() => {
        fire();
        arm();
      }, Math.min(next - now + AFTER_EDGE_MS, MAX_DELAY_MS));
    };

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      // An edge passed while the timer could not run.
      if (next !== undefined && serverDate().getTime() >= next) fire();
      arm();
    };

    arm();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [key]);
}
